
import React, { useState, useEffect } from 'react';
import { AlertCircle, RefreshCw, Database, SignalIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { toast } from './ui/sonner';
import { dbConnectionService } from '../services/dbConnectionService';
import { supabase } from '@/integrations/supabase/client';

const ConnectionStatus: React.FC = () => {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [lastCheck, setLastCheck] = useState<Date>(new Date());
  const isProd = import.meta.env.PROD;

  const checkConnection = async () => {
    setIsChecking(true);
    try {
      // First check Supabase connection - it's faster than API
      const { data, error } = await supabase.from('doctors').select('id').limit(1);
      if (!error) {
        if (!isConnected) {
          toast.success("Connection restored", {
            description: "Successfully connected to database server"
          });
        }
        setIsConnected(true);
        setIsChecking(false);
        setLastCheck(new Date());
        return;
      }
      
      // If Supabase fails, check database connection via API
      const { connected } = await dbConnectionService.verifyDatabaseConnection();
      
      // If connection status changed from disconnected to connected
      if (!isConnected && connected) {
        toast.success("Connection restored", {
          description: "Successfully connected to database server"
        });
      }
      
      setIsConnected(connected);
      setLastCheck(new Date());
    } catch (error) {
      // If connection status changed from connected to disconnected
      if (isConnected) {
        toast.error("Connection lost", {
          description: isProd ? "Unable to connect to server" : "Unable to connect to database server"
        });
      }
      setIsConnected(false);
    } finally {
      setIsChecking(false);
    }
  };

  useEffect(() => {
    checkConnection();
    
    // Set up periodic checking with shorter interval (15 seconds)
    const interval = setInterval(checkConnection, 15000); 
    
    return () => clearInterval(interval);
  }, []);

  if (isChecking && isConnected === null) {
    return (
      <div className="flex items-center justify-center p-2">
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // Only show warning when not connected
  if (!isConnected) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4 mr-2" />
        <AlertDescription className="flex justify-between items-center w-full">
          <span className="flex items-center">
            <Database className="h-4 w-4 mr-2" />
            <span>
              {isProd 
                ? "Server connection failed. Please try again later." 
                : "Database connection failed. Please check connection settings."}
            </span>
          </span>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => dbConnectionService.showConnectionHelp()}
              className="bg-white text-destructive border-destructive/50"
            >
              Help
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={checkConnection}
              className="flex gap-1 items-center"
              disabled={isChecking}
            >
              <RefreshCw className={`h-3 w-3 ${isChecking ? 'animate-spin' : ''}`} />
              {isChecking ? 'Checking...' : 'Retry Connection'}
            </Button>
          </div>
        </AlertDescription>
      </Alert>
    );
  }

  // Connected - show a small success indicator
  return (
    <div className="flex items-center mb-4 text-xs text-green-600">
      <SignalIcon className="h-3 w-3 mr-1" />
      <span>Connected</span>
      <span className="ml-1 text-gray-400">
        (Last check: {lastCheck.toLocaleTimeString()})
      </span>
    </div>
  );
};

export default ConnectionStatus;
